import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Header } from './components/Header';
import { Dashboard } from './components/Dashboard';
import { MvRegisterTable } from './components/MvRegisterTable';
import { StepTestModal } from './components/StepTestModal';
import { StepEvaluationModal } from './components/StepEvaluationModal';
import { DisturbanceLogModal } from './components/DisturbanceLogModal';
import { CorrelationMatrixView } from './components/CorrelationMatrixView';
import { ReportsModal } from './components/ReportsModal';
import { ProjectManagerModal } from './components/CampaignManagerModal';
import { ImportMvModal } from './components/ImportMvModal';
import { StepParametersModal } from './components/StepParametersModal';

import { 
  Project, 
  ManipulatedVariable, 
  StepTest, 
  Disturbance
} from './types';
import { StorageService } from './services/storage';
import { computeProjectStats } from './services/readinessCalculator';

export const App: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [currentProjectId, setCurrentProjectId] = useState<string>('kfs-apc-2026');
  const [mvs, setMvs] = useState<ManipulatedVariable[]>([]);
  const [steps, setSteps] = useState<StepTest[]>([]);
  const [disturbances, setDisturbances] = useState<Disturbance[]>([]);
  
  // App Settings
  const [selectedController, setSelectedController] = useState<string>('ALL');

  // Modals
  const [isStepModalOpen, setIsStepModalOpen] = useState<boolean>(false);
  const [stepTargetMv, setStepTargetMv] = useState<ManipulatedVariable | null>(null);
  const [evaluatingStepTest, setEvaluatingStepTest] = useState<{ step: StepTest; mv?: ManipulatedVariable } | null>(null);
  const [isDisturbanceModalOpen, setIsDisturbanceModalOpen] = useState<boolean>(false);
  const [isCorrelationModalOpen, setIsCorrelationModalOpen] = useState<boolean>(false);
  const [isReportsModalOpen, setIsReportsModalOpen] = useState<boolean>(false);
  const [isProjectManagerOpen, setIsProjectManagerOpen] = useState<boolean>(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState<boolean>(false);
  const [isParametersModalOpen, setIsParametersModalOpen] = useState<boolean>(false);
  const [highlightedMvId, setHighlightedMvId] = useState<string | null>(null);
  const [now, setNow] = useState<number>(() => Date.now());

  const hasActiveSteps = useMemo(() => {
    return steps.some((s) => s.status === 'IN_PROGRESS' && !s.completedAt);
  }, [steps]);

  const isAnyModalOpen = isStepModalOpen || !!evaluatingStepTest || isDisturbanceModalOpen || isCorrelationModalOpen || isReportsModalOpen || isProjectManagerOpen || isImportModalOpen || isParametersModalOpen;

  // Centralized 1-second timer tick so all countdowns and flashing row triggers are 100% in sync
  useEffect(() => {
    if (!hasActiveSteps || isAnyModalOpen) return;
    const interval = setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, [hasActiveSteps, isAnyModalOpen]);

  useEffect(() => {
    StorageService.init().then(() => {
      loadInitialData();
    });
  }, []);

  // Periodic background revalidation so DB updates (like deleted steps or moves) automatically reflect
  useEffect(() => {
    if (!currentProjectId) return;
    const interval = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        Promise.all([
          StorageService.getMVs(currentProjectId),
          StorageService.getStepTests(currentProjectId),
          StorageService.getDisturbances(currentProjectId),
        ])
          .then(([loadedMvs, loadedSteps, loadedDist]) => {
            setMvs(loadedMvs);
            setSteps(loadedSteps);
            setDisturbances(loadedDist);
          })
          .catch((e) => console.warn('Background sync error:', e));
      }
    }, 15000);
    return () => clearInterval(interval);
  }, [currentProjectId]);

  const loadInitialData = async () => {
    const loadedProjects = await StorageService.getProjects();
    setProjects(loadedProjects);

    const savedProjectId = StorageService.getCurrentProjectId();
    const activeProj = loadedProjects.find((p) => p.id === savedProjectId) || loadedProjects[0];
    const targetId = activeProj ? activeProj.id : '';
    setCurrentProjectId(targetId);
    if (activeProj && activeProj.id !== savedProjectId) {
      StorageService.setCurrentProjectId(activeProj.id);
    }

    if (targetId) {
      const [loadedMvs, loadedSteps, loadedDist] = await Promise.all([
        StorageService.getMVs(targetId),
        StorageService.getStepTests(targetId),
        StorageService.getDisturbances(targetId),
      ]);

      setMvs(loadedMvs);
      setSteps(loadedSteps);
      setDisturbances(loadedDist);
    } else {
      setMvs([]);
      setSteps([]);
      setDisturbances([]);
    }
  };

  const handleSelectProject = async (id: string) => {
    setCurrentProjectId(id);
    StorageService.setCurrentProjectId(id);
    setSelectedController('ALL');

    const [loadedMvs, loadedSteps, loadedDist] = await Promise.all([
      StorageService.getMVs(id),
      StorageService.getStepTests(id),
      StorageService.getDisturbances(id),
    ]);

    setMvs(loadedMvs);
    setSteps(loadedSteps);
    setDisturbances(loadedDist);
  };

  const handleUpdateMv = async (updatedMv: ManipulatedVariable, oldTag?: string) => {
    let mvToSave = updatedMv;
    if (!mvToSave.id) {
      mvToSave = {
        ...mvToSave,
        id: `mv-${currentProjectId}-${mvToSave.tag.toLowerCase().replace(/[^a-z0-9]/g, '-')}-${Date.now().toString().slice(-4)}`,
        campaignId: currentProjectId,
      };
    }
    await StorageService.updateMV(mvToSave, oldTag);
    const [updatedMvs, updatedSteps] = await Promise.all([
      StorageService.getMVs(currentProjectId),
      StorageService.getStepTests(currentProjectId),
    ]);
    setMvs(updatedMvs);
    setSteps(updatedSteps);
  };

  const handleDeleteMv = async (mvId: string) => {
    setMvs((prev) => prev.filter((m) => m.id !== mvId));
    await StorageService.deleteMV(mvId);
    const [updatedMvs, updatedSteps] = await Promise.all([
      StorageService.getMVs(currentProjectId),
      StorageService.getStepTests(currentProjectId),
    ]);
    setMvs(updatedMvs);
    setSteps(updatedSteps);
  };

  const handleCloseStepModal = useCallback(() => {
    setIsStepModalOpen(false);
  }, []);

  const handleSaveStep = useCallback(async (newStep: StepTest) => {
    await StorageService.addStepTest(newStep);
    const [updatedMvs, updatedSteps] = await Promise.all([
      StorageService.getMVs(currentProjectId),
      StorageService.getStepTests(currentProjectId),
    ]);
    setMvs(updatedMvs);
    setSteps(updatedSteps);
  }, [currentProjectId]);

  const handleAcknowledgeStep = (step: StepTest, mv: ManipulatedVariable) => {
    setEvaluatingStepTest({ step, mv });
  };

  const handleCompleteHoldEarly = async (step: StepTest) => {
    const targetMv = mvs.find((m) => m.id === step.mvId || m.tag === step.mvTag);
    const updatedStep: StepTest = {
      ...step,
      completedAt: new Date().toISOString(),
    };
    // Optimistically update steps so UI stops countdown and immediately shows Ready for Eval
    setSteps((prev) =>
      prev.map((s) => (s.id === step.id ? updatedStep : s))
    );
    setEvaluatingStepTest({ step: updatedStep, mv: targetMv });
    await StorageService.addStepTest(updatedStep);
    const updatedSteps = await StorageService.getStepTests(currentProjectId);
    setSteps(updatedSteps);
  };

  const handleEvaluateStep = async (evaluatedStep: StepTest) => {
    // Optimistically update steps and close modal immediately so timer is stopped instantly
    setSteps((prev) =>
      prev.map((s) => (s.id === evaluatedStep.id ? evaluatedStep : s))
    );
    setEvaluatingStepTest(null);

    await StorageService.evaluateStepTest(evaluatedStep);
    const [updatedMvs, updatedSteps] = await Promise.all([
      StorageService.getMVs(currentProjectId),
      StorageService.getStepTests(currentProjectId),
    ]);
    setMvs(updatedMvs);
    setSteps(updatedSteps);
  };

  const handleDeleteStep = async (stepId: string) => {
    setSteps((prev) => prev.filter((s) => s.id !== stepId));
    await StorageService.deleteStepTest(stepId);
    const [updatedMvs, updatedSteps] = await Promise.all([
      StorageService.getMVs(currentProjectId),
      StorageService.getStepTests(currentProjectId),
    ]);
    setMvs(updatedMvs);
    setSteps(updatedSteps);
  };

  const handleUpdateStep = async (step: StepTest) => {
    await StorageService.updateStepTest(step);
    const [updatedMvs, updatedSteps] = await Promise.all([
      StorageService.getMVs(currentProjectId),
      StorageService.getStepTests(currentProjectId),
    ]);
    setMvs(updatedMvs);
    setSteps(updatedSteps);
  };

  const handleAddDisturbance = async (dist: Disturbance) => {
    await StorageService.addDisturbance(dist);
    const updated = await StorageService.getDisturbances(currentProjectId);
    setDisturbances(updated);
  };

  const handleUpdateDisturbance = async (dist: Disturbance) => {
    setDisturbances((prev) => prev.map((d) => (d.id === dist.id ? dist : d)));
    await StorageService.updateDisturbance(dist);
    const updated = await StorageService.getDisturbances(currentProjectId);
    setDisturbances(updated);
  };

  const handleDeleteDisturbance = async (distId: string) => {
    await StorageService.deleteDisturbance(distId);
    const updated = await StorageService.getDisturbances(currentProjectId);
    setDisturbances(updated);
  };

  const handleOpenStepForMv = (mv: ManipulatedVariable) => {
    setStepTargetMv(mv);
    setIsStepModalOpen(true);
  };

  const handleOpenGeneralStep = () => {
    setStepTargetMv(null);
    setIsStepModalOpen(true);
  };

  const currentProject = useMemo(() => {
    return projects.find((p) => p.id === currentProjectId) || null;
  }, [projects, currentProjectId]);

  const targetMovesPerMv = currentProject?.targetMovesPerMv ?? 20;
  const defaultHoldDuration = currentProject?.defaultHoldDuration ?? 60;

  const stats = useMemo(() => {
    return computeProjectStats(currentProject?.name || 'APC Step Test Project', mvs, targetMovesPerMv);
  }, [currentProject, mvs, targetMovesPerMv]);

  const handleUpdateProjectParameters = async (newTargetMoves: number, newHoldDuration: number) => {
    if (!currentProject) return;
    const updatedProject: Project = {
      ...currentProject,
      targetMovesPerMv: newTargetMoves,
      defaultHoldDuration: newHoldDuration,
    };
    // Optimistically update projects state
    setProjects((prev) => prev.map((p) => (p.id === currentProject.id ? updatedProject : p)));
    await StorageService.updateProject(updatedProject);
    const reloadedProjects = await StorageService.getProjects();
    setProjects(reloadedProjects);
  };

  const activeTestCount = useMemo(() => {
    return mvs.filter((m) => m.status === 'Active' || m.status === 'Waiting for Steady State').length;
  }, [mvs]);

  const handleJumpToMv = useCallback((step: StepTest) => {
    const targetMv = mvs.find(
      (m) => (step.mvId && m.id === step.mvId) || (step.mvTag && m.tag.toUpperCase() === step.mvTag.toUpperCase())
    );
    const identifier = targetMv?.id || step.mvId || step.mvTag;

    // If a subcontroller tab is active that excludes this MV, switch to ALL so row is visible
    if (targetMv?.controller && selectedController !== 'ALL' && selectedController !== targetMv.controller) {
      setSelectedController('ALL');
    }

    setHighlightedMvId(identifier);

    setTimeout(() => {
      const rowEl = 
        (targetMv?.id ? document.getElementById(`mv-row-${targetMv.id}`) : null) ||
        document.getElementById(`mv-row-${step.mvTag}`) ||
        document.querySelector(`[data-mv-tag="${step.mvTag}"]`);

      if (rowEl) {
        rowEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);

    setTimeout(() => {
      setHighlightedMvId((curr) => (curr === identifier ? null : curr));
    }, 4500);
  }, [mvs, selectedController]);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 antialiased">
      {/* Header */}
      <Header
        currentProject={currentProject}
        projects={projects}
        onSelectProject={handleSelectProject}
        currentCampaign={currentProject}
        campaigns={projects}
        onSelectCampaign={handleSelectProject}
        onOpenStepModal={handleOpenGeneralStep}
        onOpenDisturbanceModal={() => setIsDisturbanceModalOpen(true)}
        onOpenCorrelationModal={() => setIsCorrelationModalOpen(true)}
        onOpenReportsModal={() => setIsReportsModalOpen(true)}
        onOpenParametersModal={() => setIsParametersModalOpen(true)}
        onOpenProjectManager={() => setIsProjectManagerOpen(true)}
        onOpenCampaignManager={() => setIsProjectManagerOpen(true)}
        onRefreshData={loadInitialData}
        activeTestCount={activeTestCount}
      />

      {/* Main Container */}
      <main className="w-full px-3 sm:px-6 lg:px-8 py-4 sm:py-6 space-y-4 sm:space-y-6 no-print">
        {/* Dashboard KPIs & Subcontroller Readiness */}
        <Dashboard
          stats={stats}
          selectedController={selectedController}
          onSelectController={setSelectedController}
          steps={steps}
          now={now}
          targetMovesPerMv={targetMovesPerMv}
          onOpenParametersModal={() => setIsParametersModalOpen(true)}
          onSelectActiveStep={handleJumpToMv}
        />

        {/* Manipulated Variable Register Data Grid */}
        <MvRegisterTable
          mvs={mvs}
          steps={steps}
          now={now}
          onUpdateMv={handleUpdateMv}
          onDeleteMv={handleDeleteMv}
          onOpenStepModalForMv={handleOpenStepForMv}
          onOpenImportModal={() => setIsImportModalOpen(true)}
          selectedController={selectedController}
          onSelectController={setSelectedController}
          projectName={currentProject?.name || 'APC Step Test Project'}
          campaignName={currentProject?.name || 'APC Step Test Project'}
          onAcknowledgeStep={handleAcknowledgeStep}
          onCompleteHoldEarly={handleCompleteHoldEarly}
          onDeleteStep={handleDeleteStep}
          onUpdateStep={handleUpdateStep}
          targetMovesPerMv={targetMovesPerMv}
          highlightedMvId={highlightedMvId}
        />
      </main>

      {/* Modals */}
      <StepTestModal
        isOpen={isStepModalOpen}
        onClose={handleCloseStepModal}
        mvs={mvs}
        initialMv={stepTargetMv}
        onSaveStep={handleSaveStep}
        currentProjectId={currentProjectId}
        currentCampaignId={currentProjectId}
        defaultHoldDuration={defaultHoldDuration}
      />

      {/* Step Evaluation & Sign-off Modal */}
      {evaluatingStepTest && (
        <StepEvaluationModal
          isOpen={true}
          onClose={() => setEvaluatingStepTest(null)}
          step={evaluatingStepTest.step}
          mv={evaluatingStepTest.mv}
          onEvaluate={handleEvaluateStep}
          onDeleteStep={handleDeleteStep}
        />
      )}

      <DisturbanceLogModal
        isOpen={isDisturbanceModalOpen}
        onClose={() => setIsDisturbanceModalOpen(false)}
        disturbances={disturbances}
        onAddDisturbance={handleAddDisturbance}
        onUpdateDisturbance={handleUpdateDisturbance}
        onDeleteDisturbance={handleDeleteDisturbance}
        currentProjectId={currentProjectId}
        currentCampaignId={currentProjectId}
        projectName={currentProject?.name || 'APC Step Test Project'}
        campaignName={currentProject?.name || 'APC Step Test Project'}
      />

      <CorrelationMatrixView
        isOpen={isCorrelationModalOpen}
        onClose={() => setIsCorrelationModalOpen(false)}
        mvs={mvs}
      />

      <ReportsModal
        isOpen={isReportsModalOpen}
        onClose={() => setIsReportsModalOpen(false)}
        projectName={currentProject?.name || 'APC Step Test Project'}
        campaignName={currentProject?.name || 'APC Step Test Project'}
        mvs={mvs}
        steps={steps}
        disturbances={disturbances}
        stats={stats}
        onDeleteStep={handleDeleteStep}
        targetMovesPerMv={targetMovesPerMv}
      />

      <ProjectManagerModal
        isOpen={isProjectManagerOpen}
        onClose={() => setIsProjectManagerOpen(false)}
        projects={projects}
        campaigns={projects}
        currentProjectId={currentProjectId}
        currentCampaignId={currentProjectId}
        onSelectProject={handleSelectProject}
        onSelectCampaign={handleSelectProject}
        onRefreshData={loadInitialData}
      />

      <ImportMvModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        projectId={currentProjectId}
        campaignId={currentProjectId}
        projectName={currentProject?.name || 'APC Step Test Project'}
        campaignName={currentProject?.name || 'APC Step Test Project'}
        onImportComplete={loadInitialData}
      />

      {/* Step Test Parameters Configuration Modal */}
      <StepParametersModal
        isOpen={isParametersModalOpen}
        onClose={() => setIsParametersModalOpen(false)}
        currentProject={currentProject}
        mvs={mvs}
        onSaveParameters={handleUpdateProjectParameters}
      />
    </div>
  );
};
